import { useEffect, useState } from "react";
import { listOpsManufacturerPayables } from "@/lib/opsManufacturerPayablesClient";
import { listOpsQbBooks } from "@/lib/opsQbBooksClient";
import { fetchSsdSales } from "@/lib/ssdSalesClient";
import { buildCashPicture } from "@/lib/ops/cashPictureMath";
import { defaultAudienceDateFrom, defaultAudienceDateTo } from "@/components/audience-data/useAudienceData";

function usd(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function OpsCashPictureStrip(props: { enabled: boolean }) {
  const [picture, setPicture] = useState<ReturnType<typeof buildCashPicture> | null>(null);

  useEffect(() => {
    if (!props.enabled) return;
    let cancelled = false;
    const from = defaultAudienceDateFrom();
    const to = defaultAudienceDateTo();
    void (async () => {
      const [pay, books, sales] = await Promise.all([
        listOpsManufacturerPayables({ status: "open" }),
        listOpsQbBooks(from, to),
        fetchSsdSales({ dateFrom: from, dateTo: to, limit: 1 }),
      ]);
      if (cancelled) return;
      const ap = pay.ok ? pay.data.totals.remaining_usd : 0;
      const po = books.ok ? books.data.report.total_usd : 0;
      const expectedIn =
        sales.ok && sales.data.aggregation
          ? sales.data.aggregation.combined.gross_usd
          : 0;
      setPicture(
        buildCashPicture({
          manufacturer_ap_remaining_usd: ap,
          po_outstanding_usd: po,
          expected_in_usd: expectedIn,
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [props.enabled]);

  if (!props.enabled || !picture) return null;

  return (
    <div className="grid gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs sm:grid-cols-4 dark:border-zinc-700 dark:bg-zinc-900">
      <div>
        <div className="text-gray-500">Manufacturer AP remaining</div>
        <div className="font-semibold text-gray-900 dark:text-zinc-100">
          {usd(picture.manufacturer_ap_remaining_usd)}
        </div>
      </div>
      <div>
        <div className="text-gray-500">PO $ this period</div>
        <div className="font-semibold text-gray-900 dark:text-zinc-100">{usd(picture.po_outstanding_usd)}</div>
      </div>
      <div>
        <div className="text-gray-500">Expected in (sales window)</div>
        <div className="font-semibold text-gray-900 dark:text-zinc-100">{usd(picture.expected_in_usd)}</div>
      </div>
      <div>
        <div className="text-gray-500">Expected out (AP + POs)</div>
        <div className="font-semibold text-gray-900 dark:text-zinc-100">{usd(picture.expected_out_usd)}</div>
      </div>
    </div>
  );
}
